import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { ModalProps } from './Modal.types'

const WIDTHS = { sm: 420, md: 560, lg: 740 }

/**
 * The app's dialog (design system: Dialog). The panel, scrim, header and
 * footer rules, and the surface-50 body, come from the theme's MuiDialog*
 * overrides; this adds the title + close button and the footer alignment.
 *
 * It used to paint a 64px primary-colour title bar, invert the close icon with
 * `filter: invert(1)` to make it white, and pad everything 36px — overriding
 * the theme on all eighteen dialogs.
 */
export const Modal = (props: ModalProps) => {
  const {
    open,
    handleClose,
    header,
    subtitle,
    footer,
    body,
    footerAlignment = 'end',
    headerCloseButton = true,
    isHeaderCloseButtonDisabled = false,
    size,
  } = props

  const justify =
    footerAlignment === 'start'
      ? 'flex-start'
      : footerAlignment === 'center'
        ? 'center'
        : 'flex-end'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      aria-labelledby={header ? 'modal-title' : undefined}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '90vh',
            ...(size && { width: WIDTHS[size], maxWidth: '95vw' }),
          },
        },
      }}
    >
      {header && (
        <DialogTitle
          id="modal-title"
          component="div"
          sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box component="h2" sx={{ m: 0, font: 'inherit' }}>
              {header}
            </Box>
            {subtitle && (
              <Box
                sx={{
                  mt: '2px',
                  fontSize: 11.5,
                  fontWeight: 400,
                  color: 'var(--ink-500)',
                }}
              >
                {subtitle}
              </Box>
            )}
          </Box>
          {headerCloseButton && (
            <IconButton
              size="small"
              aria-label="Close"
              onClick={handleClose}
              disabled={isHeaderCloseButtonDisabled}
              sx={{ mt: '-4px', mr: '-6px' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      <DialogContent>{body}</DialogContent>

      {footer && (
        <DialogActions sx={{ justifyContent: justify }}>{footer}</DialogActions>
      )}
    </Dialog>
  )
}
